import type { MessageDescriptor } from '@lingui/core';

/**
 * API error copy is either authored by us — in which case it stays an unresolved
 * `MessageDescriptor` until something holding an `i18n` instance renders it — or it is
 * a string the server sent, which we can only pass through.
 */
export type ApiErrorMessage = MessageDescriptor | string;

/**
 * The unresolved text an `Error` carries when it has no `i18n` to resolve against. The
 * production Lingui macro strips `message` off descriptors, leaving only a content hash,
 * so name the id rather than letting a bare hash become the error's title in logging.
 */
export const apiErrorMessageFallback = (message: ApiErrorMessage): string => {
  if (typeof message === 'string') {
    return message;
  }

  return message.message ?? `Localized API error (${String(message.id)})`;
};

export default class APIError extends Error {
  status: number;

  errorType?: string;

  /**
   * Set when the message is authored copy. `message` holds the source-locale text so
   * logging and generic `Error` consumers keep working; anything user-facing should
   * resolve this instead.
   */
  descriptor?: MessageDescriptor;

  constructor(message: ApiErrorMessage, status: number, errorType?: string) {
    super(apiErrorMessageFallback(message));
    this.status = status;
    this.errorType = errorType;

    if (typeof message !== 'string') {
      this.descriptor = message;
    }
  }
}
